import { Fragment, useMemo, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Pencil, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DAY_MS,
  MONTHS,
  fmtDate,
  fmtDateTime,
  isoWeek,
  ldnCompose,
  londonDateStr,
  mondayKeyOf,
  type TimeOffType,
} from '@foodstyles/shared';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useMarkTimeOffSeen } from '../../hooks/useTimeOffNotifications';
import { apiDelete, apiGet, apiPatch, apiPost, ApiError } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { Avatar, Btn, Card, EmptyState, Field, SectionTitle, Spinner, TextInput } from '../../components/primitives';
import { cn } from '../../lib/cn';
import type { TimeOffDto, TimeOffResponse } from '../../types/api';

const dateLabel = (r: TimeOffDto) => (r.start === r.end ? fmtDate(r.start) : `${fmtDate(r.start)} – ${fmtDate(r.end)}`);

// ── "Who's away" timeline ─────────────────────────────────────────────────────
const TIMELINE_DAYS = 28; // this Monday → 4 weeks out

const _wkdayFmt = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', weekday: 'narrow' });

interface TimelineDay {
  key: string; // "YYYY-MM-DD" (London)
  noon: number; // epoch ms ~ London noon (DST-safe day anchor)
  weekend: boolean;
}

const _winFmtShort = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short' });
const _winFmtFull = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
const windowLabel = (from: number, to: number) => `${_winFmtShort.format(from)} – ${_winFmtFull.format(to - 1)}`;

function WhosAwayTimeline() {
  // Window anchored once per visit (not on the live tick); offset steps in 4-week jumps.
  const [anchor] = useState(() => Date.now());
  const [offset, setOffset] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useClickOutside<HTMLDivElement>(() => setPickerOpen(false));

  // Monday-aligned 4-week window for a given offset (noon stepping is DST-safe).
  const windowFor = useMemo(() => {
    const mondayNoon0 = ldnCompose(mondayKeyOf(londonDateStr(0, anchor)), '12:00');
    return (off: number) => {
      const mondayKey = londonDateStr(0, mondayNoon0 + off * TIMELINE_DAYS * DAY_MS);
      const noon0 = ldnCompose(mondayKey, '12:00');
      const lastKey = londonDateStr(0, noon0 + (TIMELINE_DAYS - 1) * DAY_MS);
      return { noon0, from: ldnCompose(mondayKey, '00:00'), to: ldnCompose(lastKey, '00:00') + DAY_MS };
    };
  }, [anchor]);

  const { fromMs, toMs, days } = useMemo(() => {
    const w = windowFor(offset);
    const list: TimelineDay[] = Array.from({ length: TIMELINE_DAYS }, (_, i) => {
      const noon = w.noon0 + i * DAY_MS;
      const key = londonDateStr(0, noon);
      const dow = new Date(noon).getUTCDay();
      return { key, noon, weekend: dow === 0 || dow === 6 };
    });
    return { fromMs: w.from, toMs: w.to, days: list };
  }, [windowFor, offset]);

  // Dropdown: jumpable 4-week windows around today (2 back, current, 4 ahead).
  const windows = useMemo(
    () => Array.from({ length: 7 }, (_, i) => i - 2).map((off) => ({ off, ...windowFor(off) })),
    [windowFor],
  );

  const cal = useQuery({
    queryKey: ['timeoff', 'calendar', fromMs, toMs],
    queryFn: () => apiGet<TimeOffResponse>(`/api/timeoff/calendar?from=${fromMs}&to=${toMs}`),
  });

  const people = useMemo(() => {
    const map = new Map<string, { name: string; requests: TimeOffDto[] }>();
    for (const r of cal.data?.requests ?? []) {
      if (!map.has(r.userId)) map.set(r.userId, { name: r.userName, requests: [] });
      map.get(r.userId)!.requests.push(r);
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [cal.data]);

  const todayKey = londonDateStr(0, anchor);
  // start/end are London midnights of the first/last day, so a day belongs to the
  // request when its noon falls inside [start, end + 24h].
  const coverage = (requests: TimeOffDto[], noon: number) =>
    requests.find((r) => noon >= r.start && noon <= r.end + DAY_MS);

  return (
    <Card pad={false}>
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
        <SectionTitle sub="Approved time off across the team — 4-week view.">Who's away</SectionTitle>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm bg-[#FF5A3C]/85" /> Full day off
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm bg-amber-400" /> Working less
            </span>
          </div>
          <div className="relative inline-flex items-stretch" ref={pickerRef}>
            <button
              onClick={() => setPickerOpen((o) => !o)}
              aria-haspopup="listbox"
              aria-expanded={pickerOpen}
              className="flex items-center gap-2 rounded-l-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <Calendar size={14} className="text-slate-400" />
              {offset === 0 ? 'This 4 weeks' : windowLabel(fromMs, toMs)}
            </button>
            <button
              onClick={() => setOffset(offset - 1)}
              aria-label="Previous 4 weeks"
              className="border-y border-slate-200 bg-white px-1.5 text-slate-500 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700"
            >
              <ChevronLeft size={15} />
            </button>
            <button
              onClick={() => setOffset(offset + 1)}
              aria-label="Next 4 weeks"
              className="rounded-r-lg border border-y border-r border-slate-200 bg-white px-1.5 text-slate-500 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700"
            >
              <ChevronRight size={15} />
            </button>
            {pickerOpen && (
              <div className="absolute right-0 top-full z-40 mt-1 w-64 rounded-lg border border-slate-200 bg-white p-1.5 shadow-lg dark:border-slate-700 dark:bg-slate-800">
                {windows.map((w) => (
                  <button
                    key={w.off}
                    onClick={() => {
                      setOffset(w.off);
                      setPickerOpen(false);
                    }}
                    className={cn(
                      'block w-full rounded-md px-3 py-1.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700',
                      w.off === offset ? 'font-semibold text-[#D8341A] dark:text-[#FF8A6B]' : 'text-slate-600 dark:text-slate-300',
                    )}
                  >
                    {windowLabel(w.from, w.to)}
                    {w.off === 0 && <span className="ml-1.5 text-[10px] font-semibold text-slate-400">current</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {cal.isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : people.length === 0 ? (
        <EmptyState>No approved time off in this 4-week window.</EmptyState>
      ) : (
        <div className="overflow-x-auto pb-2">
          <div
            className="grid min-w-[820px] items-center"
            style={{ gridTemplateColumns: `150px repeat(${TIMELINE_DAYS}, minmax(20px, 1fr))` }}
          >
            {/* header row */}
            <div className="border-b border-slate-100 dark:border-slate-700" />
            {days.map((d, i) => {
              const dayNum = Number(d.key.slice(8, 10));
              const showMonth = dayNum === 1 || d.key === days[0].key;
              const isToday = d.key === todayKey;
              const isMonday = i % 7 === 0;
              return (
                <div
                  key={d.key}
                  className={`border-b border-slate-100 py-1.5 text-center dark:border-slate-700 ${d.weekend ? 'bg-slate-50/70 dark:bg-slate-800/60' : ''}`}
                >
                  <div className="h-3 text-[9px] font-bold text-slate-400">{isMonday ? `W${isoWeek(d.noon)}` : ''}</div>
                  <div className={`text-[9px] uppercase ${isToday ? 'font-bold text-[#D8341A] dark:text-[#FF8A6B]' : 'text-slate-400'}`}>
                    {_wkdayFmt.format(d.noon)}
                  </div>
                  <div className={`text-[11px] tabular-nums ${isToday ? 'font-bold text-[#D8341A] dark:text-[#FF8A6B]' : 'text-slate-500 dark:text-slate-400'}`}>
                    {dayNum}
                  </div>
                  {showMonth && (
                    <div className="text-[9px] font-semibold text-slate-400">{MONTHS[Number(d.key.slice(5, 7)) - 1]}</div>
                  )}
                </div>
              );
            })}

            {/* one row per person with approved time off */}
            {people.map((p) => (
              <Fragment key={p.name}>
                <div className="truncate border-b border-slate-50 px-5 py-2 text-sm font-medium text-slate-700 dark:border-slate-700 dark:text-slate-200">
                  {p.name}
                </div>
                {days.map((d) => {
                  const r = coverage(p.requests, d.noon);
                  return (
                    <div
                      key={d.key}
                      className={`flex h-8 items-center border-b border-slate-50 px-[1px] dark:border-slate-700 ${d.weekend ? 'bg-slate-50/70 dark:bg-slate-800/60' : ''}`}
                    >
                      {r && (
                        <div
                          title={`${r.type}: ${dateLabel(r)}${r.reason ? ` — ${r.reason}` : ''}`}
                          className={`h-4 w-full rounded-sm ${
                            r.type === 'Full day off' ? 'bg-[#FF5A3C]/85' : 'bg-amber-400'
                          }`}
                        />
                      )}
                    </div>
                  );
                })}
              </Fragment>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

export function TimeOffScreen() {
  const { permissions } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  useMarkTimeOffSeen(); // clears the sidebar "new requests" badge

  const mine = useQuery({ queryKey: qk.timeoff('mine'), queryFn: () => apiGet<TimeOffResponse>('/api/timeoff?scope=mine') });
  const team = useQuery({
    queryKey: qk.timeoff('team'),
    queryFn: () => apiGet<TimeOffResponse>('/api/timeoff?scope=team'),
    enabled: !!permissions?.approveTimeOff,
  });

  const invalidate = () => {
    // Covers mine / team / the "Who's away" calendar in one go.
    void qc.invalidateQueries({ queryKey: ['timeoff'] });
  };

  type TimeOffBody = { type: TimeOffType; mode: string; start: number; end?: number; reason: string };

  const submit = useMutation({
    mutationFn: (b: TimeOffBody) => apiPost('/api/timeoff', b),
    onSuccess: () => {
      invalidate();
      toast('Time off added — it appears on the timeline right away.', 'success');
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : 'Could not submit request.'),
  });

  const update = useMutation({
    mutationFn: (v: { id: string; body: TimeOffBody }) => apiPatch(`/api/timeoff/${v.id}`, v.body),
    onSuccess: () => {
      invalidate();
      toast('Time off updated.', 'success');
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : 'Could not update the request.'),
  });

  const del = useMutation({
    mutationFn: (id: string) => apiDelete(`/api/timeoff/${id}`),
    onSuccess: () => {
      invalidate();
      toast('Time off deleted.', 'success');
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : 'Could not delete the request.'),
  });

  const [type, setType] = useState<TimeOffType>('Full day off');
  const [mode, setMode] = useState<'single' | 'range'>('single');
  const [d1, setD1] = useState(() => londonDateStr(0));
  const [d2, setD2] = useState(() => londonDateStr(0));
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState<TimeOffDto | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  const resetForm = () => {
    setEditing(null);
    setType('Full day off');
    setMode('single');
    setD1(londonDateStr(0));
    setD2(londonDateStr(0));
    setReason('');
    setErr('');
  };

  const startEdit = (r: TimeOffDto) => {
    setEditing(r);
    setType(r.type);
    setMode(r.start === r.end ? 'single' : 'range');
    setD1(londonDateStr(0, r.start));
    setD2(londonDateStr(0, r.end));
    setReason(r.reason);
    setErr('');
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onDeleteRequest = (r: TimeOffDto) => {
    const ok = confirm(
      `Delete this time off?\n\n${r.type} · ${dateLabel(r)}${r.reason ? `\n${r.reason}` : ''}\n\nIt comes off the timeline right away.`,
    );
    if (!ok) return;
    del.mutate(r.id, {
      onSuccess: () => {
        if (editing?.id === r.id) resetForm();
      },
    });
  };

  const onSubmit = () => {
    if (mode === 'range' && ldnCompose(d2, '00:00') < ldnCompose(d1, '00:00')) {
      setErr("End date can't be before start date.");
      return;
    }
    setErr('');
    const body: TimeOffBody = {
      type,
      mode,
      start: ldnCompose(d1, '00:00'),
      end: mode === 'range' ? ldnCompose(d2, '00:00') : undefined,
      reason: reason.trim(),
    };
    if (editing) update.mutate({ id: editing.id, body }, { onSuccess: resetForm });
    else submit.mutate(body, { onSuccess: () => setReason('') });
  };

  const todayKey = londonDateStr(0);

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <WhosAwayTimeline />
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
      <div ref={formRef} className="scroll-mt-20 lg:col-span-2">
      <Card className="space-y-4">
        <SectionTitle
          sub={
            editing
              ? 'Changes apply to the timeline right away — admins are notified.'
              : 'Approved automatically — admins are notified.'
          }
        >
          {editing ? 'Edit time off' : 'Request time off'}
        </SectionTitle>
        <Field label="Type">
          <div className="grid grid-cols-2 gap-2">
            {(['Full day off', 'Working less'] as TimeOffType[]).map((t) => (
              <button
                key={t}
                onClick={() => setType(t)}
                className={cn(
                  'rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors',
                  type === t ? 'border-[#FF5A3C] bg-[#FF5A3C]/10 text-[#D8341A] dark:bg-[#FF5A3C]/20 dark:text-[#FF8A6B]' : 'border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700',
                )}
              >
                {t}
              </button>
            ))}
          </div>
        </Field>
        <Field label="When">
          <div className="grid grid-cols-2 gap-2">
            {([['single', 'Single day'], ['range', 'Date range']] as const).map(([m, l]) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={cn(
                  'rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors',
                  mode === m ? 'border-[#FF5A3C] bg-[#FF5A3C]/10 text-[#D8341A] dark:bg-[#FF5A3C]/20 dark:text-[#FF8A6B]' : 'border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700',
                )}
              >
                {l}
              </button>
            ))}
          </div>
        </Field>
        {mode === 'single' ? (
          <Field label="Date">
            <TextInput type="date" value={d1} onChange={(e) => setD1(e.target.value)} />
          </Field>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <Field label="From">
              <TextInput type="date" value={d1} onChange={(e) => setD1(e.target.value)} />
            </Field>
            <Field label="To">
              <TextInput type="date" value={d2} onChange={(e) => setD2(e.target.value)} />
            </Field>
          </div>
        )}
        <Field label="Reason (optional)">
          <textarea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#FF5A3C] focus:outline-none focus:ring-2 focus:ring-[#FF5A3C]/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          />
        </Field>
        {err && <p className="text-sm text-rose-600 dark:text-rose-400">{err}</p>}
        {editing ? (
          <div className="flex gap-2">
            <Btn variant="primary" className="flex-1" onClick={onSubmit} disabled={update.isPending || del.isPending}>
              Save changes
            </Btn>
            <Btn onClick={resetForm} disabled={update.isPending}>
              Cancel
            </Btn>
          </div>
        ) : (
          <Btn variant="primary" className="w-full" onClick={onSubmit} disabled={submit.isPending}>
            Submit request
          </Btn>
        )}
      </Card>
      </div>

      <div className="space-y-5 lg:col-span-3">
        {permissions?.approveTimeOff && (
          <Card pad={false}>
            <div className="px-5 py-4">
              <SectionTitle sub="Admin only — the latest submissions across the team (all auto-approved).">
                Team requests
              </SectionTitle>
            </div>
            <div className="max-h-[320px] divide-y divide-slate-100 overflow-y-auto dark:divide-slate-700">
              {team.isLoading ? (
                <div className="flex justify-center py-6">
                  <Spinner />
                </div>
              ) : (team.data?.requests.length ?? 0) === 0 ? (
                <EmptyState>No requests yet.</EmptyState>
              ) : (
                team.data!.requests.map((r) => (
                  <div key={r.id} className="flex items-center gap-3 px-5 py-3">
                    <Avatar name={r.userName} size={34} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">{r.userName}</div>
                      <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                        {r.type} · {dateLabel(r)}
                        {r.reason ? ` · ${r.reason}` : ''}
                      </div>
                    </div>
                    <span className="text-[11px] text-slate-400">{fmtDateTime(r.created)}</span>
                    {londonDateStr(0, r.created) === todayKey && (
                      <span className="inline-flex items-center rounded-full bg-[#FF5A3C]/10 px-2.5 py-0.5 text-xs font-semibold text-[#D8341A] dark:bg-[#FF5A3C]/20 dark:text-[#FF8A6B]">
                        New
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
          </Card>
        )}

        <Card pad={false}>
          <div className="px-5 py-4">
            <SectionTitle>My requests</SectionTitle>
          </div>
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {mine.isLoading ? (
              <div className="flex justify-center py-6">
                <Spinner />
              </div>
            ) : (mine.data?.requests.length ?? 0) === 0 ? (
              <EmptyState>You haven't requested any time off.</EmptyState>
            ) : (
              mine.data!.requests.map((r) => (
                <div
                  key={r.id}
                  className={cn('flex items-center gap-3 px-5 py-3', editing?.id === r.id && 'bg-[#FF5A3C]/5 dark:bg-[#FF5A3C]/10')}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      {r.type} · {dateLabel(r)}
                    </div>
                    <div className="text-xs text-slate-400">
                      Requested {fmtDateTime(r.created)}
                      {r.decidedByName ? ` · by ${r.decidedByName}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <button
                      onClick={() => startEdit(r)}
                      disabled={update.isPending || del.isPending}
                      aria-label="Edit request"
                      className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-30 dark:hover:bg-slate-700 dark:hover:text-slate-300"
                    >
                      <Pencil size={15} />
                    </button>
                    <button
                      onClick={() => onDeleteRequest(r)}
                      disabled={update.isPending || del.isPending}
                      aria-label="Delete request"
                      className="rounded p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-30 dark:hover:bg-rose-500/10 dark:hover:text-rose-400"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
      </div>
    </div>
  );
}
